import { IonItem, IonList } from '@ionic/react';
import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import usePlacesService from 'react-google-autocomplete/lib/usePlacesAutocompleteService';

import ENVIRONMENT from '@/environments/environment';
import { PostalAddress } from '@/interfaces/CustomerInterface/CustomerInterface';
import { getRoadType } from '@/utils/formHelper';

import styles from './PlacesAutocomplete.module.scss';

interface Props {
  onSelect: (addressData: PostalAddress, completeAddress?: boolean) => void;
  onZeroResults?: (value: boolean) => void;
  defaultValue?: string;
  placeholder?: string;
  showBorder?: boolean;
  addressError?: boolean;
  onIonInput?: (value: boolean) => void;
}
export default function LocationSearchInput({
  onSelect, defaultValue = '', onZeroResults = () => { }, placeholder = 'Buscar dirección...', showBorder = false,
  addressError = false,
  onIonInput = () => { },
}: Props) {
  const [address, setAddress] = useState(defaultValue);
  const [zeroResults, setZeroResults] = useState(false);
  const [predictions, setPredictions] = useState<any[]>([]);
  const aux = useRef('');
  const [isFromDefaultValue, setIsFromDefaultValue] = useState(true);
  const autocomplete = useRef<boolean>(false);

  const {
    placesService,
    placePredictions,
    getPlacePredictions,
  } = usePlacesService({
    apiKey: ENVIRONMENT.MAPS_API_KEY,
    options: {
      input: address,
      componentRestrictions: {
        country: 'ES',
      },
      types: ['address'],
    },
  });

  const handleSelect = (place: any) => {
    if (place) {
      const addressComponents = {
        street_number: place.address_components?.find(
          (component: any) => component.types.includes('street_number'),
        )?.long_name || '',
        route: place.address_components?.find(
          (component: any) => component.types.includes('route'),
        )?.long_name || '',
        city: place.address_components?.find(
          (component: any) => component.types.includes('locality'),
        )?.long_name || '',
        postal_code: place.address_components?.find(
          (component: any) => component.types.includes('postal_code'),
        )?.long_name || '',
      };

      const roadType = addressComponents.route.split(' ')[0] || '';
      const foundAddress = `${addressComponents.route} ${addressComponents.street_number}`;

      const addressData: PostalAddress = {
        address: foundAddress,
        city: addressComponents.city,
        number: addressComponents.street_number,
        postal_code: addressComponents.postal_code,
        road_type: getRoadType(roadType),
        province: addressComponents.city,
        aux: aux.current,
      };

      setAddress(place.formatted_address);
      onSelect(addressData, true);
      setPredictions([]);
      // onZeroResults(false);
      setZeroResults(false);
      setIsFromDefaultValue(false);
    }
  };

  const getPlaceDetails = (placeId: string) => {
    placesService?.getDetails(
      {
        placeId,
      },
      (placeDetails) => {
        handleSelect(placeDetails);
      },
    );
  };

  useEffect(() => {
    if (placePredictions.length) {
      setPredictions(placePredictions);
    }
  }, [placePredictions]);

  const formatAddress = (toFormatAddress: string) => {
    const dir1Match = toFormatAddress.match(/^(.*?)\s*(\d+)/);
    const street = dir1Match?.[1];
    const number = dir1Match?.[2].replace(/^0+/, '');
    if (street) {
      const dir1 = `${street} ${number}`.trim();
      const auxDir = toFormatAddress.match(/\d+\s+(.*?)\s+(?=-)/)?.[1];
      if (auxDir) {
        aux.current = auxDir;
      }
      const dir3 = toFormatAddress.split('-').slice(-2).map((part) => part.trim()).join(', ');
      return `${dir1}, ${dir3}`;
    }
    return undefined;
  };

  useEffect(() => {
    if (defaultValue) {
      const formattedAddress = formatAddress(defaultValue);
      if (formattedAddress) {
        setAddress(formattedAddress);

        // Obtener predicciones basadas en la dirección formateada
        autocomplete.current = true;
        getPlacePredictions({
          input: formattedAddress,
          componentRestrictions: { country: 'ES' },
          types: ['address'],
        });
        showBorder && setAddress(formattedAddress);
      } else {
        // Manejar el caso de resultados cero
        setIsFromDefaultValue(false);
        onZeroResults(true);
        setZeroResults(true);
        onSelect({
          address: defaultValue,
          city: '',
          number: '',
          postal_code: '',
          road_type: '',
          province: '',
        });
      }
    } else {
      setIsFromDefaultValue(false);

      onSelect({
        address: '',
        city: '',
        number: '',
        postal_code: '',
        road_type: '',
        province: '',
      });
    }
  }, [defaultValue]);

  useEffect(() => {
    if (isFromDefaultValue && placePredictions.length > 0 && autocomplete.current) {
      autocomplete.current = false;
      const firstPrediction = placePredictions[0];
      if (firstPrediction) {
        getPlaceDetails(firstPrediction.place_id);
      }
      // setIsFromDefaultValue(false); // Resetear el estado después de usarlo
    } else {
      // onZeroResults(true);
      // setZeroResults(true);
      // autocomplete.current = false;
      /* onSelect({
        address: formatAddress(address) || '',
        city: '',
        number: '',
        postal_code: '',
        road_type: '',
        province: '',
      }); */
    }
  }, [placePredictions]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsFromDefaultValue(false);
    setAddress(e.target.value);
    onIonInput(true);
    if (e.target.value === '') {
      setZeroResults(true);
      onZeroResults(true);
      onSelect({
        address: '',
        city: '',
        number: '',
        postal_code: '',
        road_type: '',
        province: '',
      });
    } else {
      setZeroResults(false);
      onZeroResults(false);
    }
  };

  return (
    <div className={styles.placesAutocompleteContainer}>
      <div>
        <input
          placeholder={placeholder}
          value={address}
          onChange={(evt) => {
            handleChange(evt);
            getPlacePredictions({
              input: evt.target.value,
              componentRestrictions: { country: 'ES' },
              types: ['address'],
            });
          }}
          className={classNames(
            { [styles.inputFirstForm]: showBorder },
            { [styles.inputFirstFormError]: showBorder && addressError },
            styles.systemFont,
            styles.locationSearchInput,
            styles.upperInput,
            { 'ion-invalid': zeroResults },
          )}
        />
        {addressError && (
          <div className={styles.errorMessage}>
            <span>Indica una dirección válida</span>
          </div>
        )}

        <IonList className={styles.autocompleteDropdownContainer}>
          {predictions.length > 0 && predictions.map((suggestion) => (
            <IonItem
              className={styles.suggestionItem}
              key={suggestion.place_id}
              lines="full"
              button
              onClick={() => getPlaceDetails(suggestion.place_id)}
            >
              <div className="suggestion-text">
                {suggestion.description}
              </div>
            </IonItem>
          ))}
        </IonList>
      </div>
    </div>
  );
}
